import { intersection } from 'lodash';
import type { Nullable } from 'types';
import type { CampaignDTO } from 'utils/campaign/mappers';
import { SubSourcesConfig } from 'utils/campaign/requirementUtils';
import { SourceConfigDto, SourceConfigToDto } from 'utils/campaign/requirementUtils/types';
import { EMPTY_ARRAY } from 'utils/clickme';
import { BANNER_ID_URL_FIELD_NAME, BannerImageCropType } from 'utils/constants/banner';
import {
    CAMPAIGN_ID_URL_FIELD_NAME,
    CAMPAIGNS_CURRENT_TAB_URL_FIELD_NAME,
    CampaignSubSource,
    CampaignSubSourceDisableReason,
    HH_SPLIT_SUB_SOURCES,
    SUBSOURCE_TO_SOURCE_MAPPING,
} from 'utils/constants/campaign';
import { CAMPAIGN_MAX_ITEMS_IN_RESPONSE } from 'utils/constants/dashboard';

import type { Match } from 'app/routes';

import type { Region, RegionBE } from 'store/autoReducers';
import { AuctionCampaign, AuctionCampaignStatus } from 'store/models/auctionData';
import { CampaignsTab, DEFAULT_PAGE_NUMBER } from 'store/models/campaignsDashboard';
import { Images } from 'store/models/createBanner';
import { Source } from 'store/models/createCampaign';
import { CroppedImages } from 'store/models/createQuickCampaign';
import { type Banner, CampaignLoadState } from 'store/models/dashboard';
import { ImageCreatedBy } from 'store/models/gallery';

export const getCampaignIdFromMatch = (match: Match['match']): Nullable<number> =>
    match?.params?.campaignId ? parseInt(match?.params?.[CAMPAIGN_ID_URL_FIELD_NAME], 10) : null;
export const getBannerIdFromMatch = (match: Match['match']): Nullable<number> =>
    match?.params.bannerId ? parseInt(match?.params?.[BANNER_ID_URL_FIELD_NAME], 10) : null;
export const getVacancyIdFromMatch = (match: Match['match']): Nullable<string> =>
    match?.params.vacancyId ? match?.params?.vacancyId : null;
export const getCampaignsCurrentTabFromMatch = (match: Match['match']): CampaignsTab => {
    const campaignsCurrentTab = match?.params?.[CAMPAIGNS_CURRENT_TAB_URL_FIELD_NAME]?.split('?')[0];
    return campaignsCurrentTab && Object.values(CampaignsTab).includes(campaignsCurrentTab as CampaignsTab)
        ? (campaignsCurrentTab as CampaignsTab)
        : CampaignsTab.Active;
};

export const getCampaignsPageNumberFromUrl = (url?: string): number => {
    const queryString = url?.split('?')[1] || window.location.search;
    const page = Number(new URLSearchParams(queryString).get('page'));
    return page > 0 ? page : DEFAULT_PAGE_NUMBER;
};

export const getUrlToBanners = (campaignId: number): string => `/new/campaign/${campaignId}/banners`;

export const getBannerIdForCopy = (banners: Banner[]): Banner['id'] => banners.slice(-1)[0].id;

export const sourcesConfigFromDto = (sourceConfig: SourceConfigDto[]): SubSourcesConfig => ({
    originalSourcesConfig: sourceConfig,
    enabledSources: sourceConfig.filter(({ enabled }) => enabled).map(({ subSource }) => subSource),
});

export const sourcesConfigToDto = (sources: CampaignSubSource[]): SourceConfigToDto[] =>
    [...new Set(sources.map((subSourceItem) => SUBSOURCE_TO_SOURCE_MAPPING[subSourceItem]))].map((sourceItem) => ({
        apiSource: sourceItem,
        subSources: sources.filter((subSourceItem) => sourceItem === SUBSOURCE_TO_SOURCE_MAPPING[subSourceItem]),
    }));

export const sourcesConfigToTree = (sourceConfig: SourceConfigDto[]): Source[] => {
    return sourceConfig.reduce((arr, sourceConfigItem) => {
        const { apiSource, ...subSources } = sourceConfigItem;
        const currentSource = arr.find((item) => item.apiSource === apiSource);
        if (!currentSource) {
            arr.push({
                apiSource,
                subSources: [{ ...subSources }],
            });
        } else {
            currentSource.subSources.push({
                ...subSources,
            });
        }
        return arr;
    }, [] as Source[]);
};

export const convertRegionsToDto = (regions: string[]): number[] => regions.map((region) => parseInt(region, 10));

export const convertRegionsFromDto = (regions: RegionBE[]): Region[] =>
    regions.map((region) => ({
        ...region,
        id: String(region.id),
        areas: region.areas.length ? convertRegionsFromDto(region.areas) : EMPTY_ARRAY,
    }));

export const convertRegionsToMap = (regions: RegionBE[]): Record<number, string> =>
    regions.reduce<Record<number, string>>((acc, region) => {
        acc[region.id] = region.name;

        if (region.areas.length) {
            Object.assign(acc, convertRegionsToMap(region.areas));
        }

        return acc;
    }, {});

export const convertCampaignSourcesFromDto = (campaignSources: SourceConfigToDto[]): CampaignSubSource[] =>
    campaignSources.reduce((sourceList: CampaignSubSource[], source) => [...sourceList, ...source.subSources], []);

// exp_PORTFOLIO-53635_start
/**
 * При копировании старой кампании прежний единый сабсорс HH заменяет на актуальные сабсорсы сплита из конфига.
 *
 * Без этого старый HH остаётся в выборке (его нет в новом сплит-конфиге, поэтому он не отображается чекбоксом),
 * и после выбора площадки HH в выборке оказываются сразу три сабсорса: HH, HH_TOP_SEARCH и HH_BANNERS.
 * Если сплит в конфиге не активен или HH в выборке нет, исходный список возвращается без изменений.
 */
export const replaceHHWithSplitSubSources = (
    sources: CampaignSubSource[],
    sourcesConfig: SourceConfigDto[]
): CampaignSubSource[] => {
    const hasSplitConfig = sourcesConfig.some(({ subSource }) => HH_SPLIT_SUB_SOURCES.includes(subSource));

    if (!hasSplitConfig || !sources.includes(CampaignSubSource.HH)) {
        return sources;
    }

    const enabledSplitSubSources = sourcesConfig
        .filter(({ enabled, subSource }) => enabled && HH_SPLIT_SUB_SOURCES.includes(subSource))
        .map(({ subSource }) => subSource);

    const sourcesWithoutHHSubsource = sources.filter((source) => source !== CampaignSubSource.HH);

    return [...new Set([...sourcesWithoutHHSubsource, ...enabledSplitSubSources])];
};
// exp_PORTFOLIO-53635_end

export const getStatusByCampaigns = (campaigns: CampaignDTO[]): CampaignLoadState =>
    campaigns.length < CAMPAIGN_MAX_ITEMS_IN_RESPONSE ? CampaignLoadState.Disabled : CampaignLoadState.Success;

export function findRegionsByIds(ids: string[], regions: Region[], onlyNames: true): string[];
export function findRegionsByIds(ids: string[], regions: Region[], onlyNames?: boolean): Region[];
export function findRegionsByIds(ids: string[], regions: Region[], onlyNames?: boolean): (Region | string)[] {
    const idsSet = new Set(ids);
    const result: (Region | string)[] = [];

    regions.some((region) => {
        if (idsSet.has(region.id)) {
            idsSet.delete(region.id);
            result.push(onlyNames ? region.name : region);
        }

        if (region.areas.length && idsSet.size) {
            result.push(...findRegionsByIds(ids, region.areas, onlyNames));
        }

        return !idsSet.size;
    });

    return result;
}

export const isValidAreaId = (id: unknown): id is number => isFinite(parseInt(String(id), 10));

export const isAiGalleryImage = (createdBy: Nullable<ImageCreatedBy>): boolean => {
    return (
        createdBy === ImageCreatedBy.AiStableDiffusion ||
        createdBy === ImageCreatedBy.AiMidjourney ||
        createdBy === ImageCreatedBy.AiFlux
    );
};

// exp_PORTFOLIO-53635_next_line
const DISCOUNTED_SOURCES = [CampaignSubSource.HH, CampaignSubSource.HHTopSearch, CampaignSubSource.HHBanners];
export const isDiscountedSources = (adSource: CampaignSubSource[]): boolean => {
    let isDiscounted = false;
    for (const source of adSource) {
        if (DISCOUNTED_SOURCES.includes(source)) {
            isDiscounted = true;
        } else {
            return false;
        }
    }
    return isDiscounted;
};

export const getSourcesDisableReason = (adSourcesConfig: SourceConfigDto[]): CampaignSubSourceDisableReason | null => {
    const disableReasons = new Set(
        adSourcesConfig.map((source) => source.disableReason).filter((disableReason) => disableReason)
    );

    if (
        (disableReasons.has(CampaignSubSourceDisableReason.LowBalance) &&
            disableReasons.has(CampaignSubSourceDisableReason.LowImgRes)) ||
        disableReasons.has(CampaignSubSourceDisableReason.LowImgResAndLowBalance)
    ) {
        return CampaignSubSourceDisableReason.LowImgResAndLowBalance;
    }

    if (disableReasons.has(CampaignSubSourceDisableReason.LowBalance)) {
        return CampaignSubSourceDisableReason.LowBalance;
    }

    if (disableReasons.has(CampaignSubSourceDisableReason.LowImgRes)) {
        return CampaignSubSourceDisableReason.LowImgRes;
    }

    return null;
};

export const getAllSourcesChecked = (
    checkedSources: CampaignSubSource[],
    enabledSources: CampaignSubSource[]
): boolean => {
    return intersection(checkedSources.sort(), enabledSources.sort()).length === enabledSources.length;
};

export const getAuctionTrlState = (auctionCampaign: AuctionCampaign | null): 'create' | 'edit' | 'restart' => {
    if (
        auctionCampaign &&
        auctionCampaign?.campaignStatus !== AuctionCampaignStatus.Active &&
        auctionCampaign?.campaignStatus !== AuctionCampaignStatus.ActiveWithDelayedStart
    ) {
        return 'restart';
    }
    if (auctionCampaign) {
        return 'edit';
    }
    return 'create';
};

type GetAbsValue = (dimension: number, percent: number) => number;
const getAbsValue: GetAbsValue = (dimension, percent) => Math.trunc((percent * dimension) / 100);

export const getCropInfoForQuickCampaign = (images: Images): CroppedImages => {
    const cropInfo: CroppedImages = [];

    Object.entries(images).forEach(([cropType, image]) => {
        const { id, width, height } = image;
        const { percentCrop } = image.cropInfo || {};
        if (percentCrop?.width && width && height && id) {
            const percentCropX = percentCrop.x > 0 ? percentCrop.x : 0;
            const percentCropY = percentCrop.y > 0 ? percentCrop.y : 0;
            cropInfo.push({
                originalImageId: id,
                cropType: cropType as BannerImageCropType,
                x: getAbsValue(width, percentCropX),
                y: getAbsValue(height, percentCropY),
                imageWidth: getAbsValue(width, percentCrop.width),
                imageHeight: getAbsValue(height, percentCrop.height),
            });
        }
    });

    return cropInfo;
};
